import { useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import {
  editorialImage,
  sourcesForEditorial,
  type CapabilityReading,
  type EditorialTopic,
} from './scenario-capability-editorial';
import { modalDismissProps, useModalFocus } from './scenario-modal-focus';

export function EditorialCapabilityImage({
  path,
  title,
}: {
  readonly path: string | undefined;
  readonly title: string;
}) {
  const src = path ? editorialImage(path) : undefined;
  const [failed, setFailed] = useState<string | null>(null);
  return (
    <figure className="scc-editorial-image">
      {src && failed !== src ? (
        <img
          src={src}
          alt={`${title} · AI生成技术示意 · 非实拍、非具体型号`}
          width="1280"
          height="720"
          loading="lazy"
          decoding="async"
          onError={() => setFailed(src)}
        />
      ) : (
        <div className="scc-editorial-image-fallback" role="status">
          {title}
          <br />
          {src
            ? '示意图未能加载，请阅读能力说明。'
            : '暂无与完整需求匹配的示意图，请阅读原需求说明。'}
        </div>
      )}
    </figure>
  );
}

/** One card used by both existing scene readers. Relations are supplied by their owners. */
export function ScenarioCapabilityReadingCard({
  reading,
  onOpen,
}: {
  readonly reading: CapabilityReading;
  readonly onOpen: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <>
      <div className="scc-cap-art">
        <EditorialCapabilityImage path={reading.topic?.image} title={reading.title} />
      </div>
      <div className="scc-cap-body">
        <strong>{reading.title}</strong>
        {reading.pending && (
          <span className="scc-cap-scope" data-scope-marker>
            专业说明待审核 · 研究说明
          </span>
        )}
        <p className="scc-cap-purpose">{reading.purpose}</p>
        <p className="scc-cap-output">可获得：{reading.output}</p>
        <button className="scc-text-button" onClick={onOpen}>
          了解能力与实际示例 →
        </button>
      </div>
    </>
  );
}

export function EditorialSources({ topic }: { readonly topic: EditorialTopic }) {
  const sources = sourcesForEditorial(topic);
  const rows = (items: typeof sources) =>
    items.map((source) => (
      <li key={source.id} data-editorial-source={source.id}>
        <strong>{source.name}</strong>
        <span className="scc-drawer-source-note">
          {source.publisher} · {source.kind}
        </span>
        <p>{source.whatSupports}</p>
        {source.kind.includes('厂商') && (
          <p className="scc-drawer-note">技术示例，不是已部署装备</p>
        )}
        <a href={source.url} target="_blank" rel="noopener noreferrer">
          {source.kind.includes('视频') ? '查看官网演示入口' : '查看原文'} ↗
        </a>
      </li>
    ));
  return (
    <>
      <ul className="scc-drawer-list">{rows(sources.slice(0, 2))}</ul>
      {sources.length > 2 && (
        <details>
          <summary>展开其他资料（{sources.length - 2}）</summary>
          <ul className="scc-drawer-list">{rows(sources.slice(2))}</ul>
        </details>
      )}
      <p className="scc-drawer-note">官方原文外链；目标网络与视频播放未验证。</p>
    </>
  );
}

/** The existing modal focus/scroll/backdrop contract remains the only modal implementation. */
export function ScenarioCapabilityReadingDrawer({
  reading,
  eyebrow,
  restoreFocusTo,
  onClose,
  children,
}: {
  readonly reading: CapabilityReading;
  readonly eyebrow: string;
  readonly restoreFocusTo: HTMLElement | null | undefined;
  readonly onClose: () => void;
  readonly children?: ReactNode;
}) {
  const dialog = useModalFocus(restoreFocusTo);
  return (
    <dialog
      className="scc-drawer scc-capability-drawer"
      ref={dialog}
      aria-modal="true"
      aria-labelledby="scc-capability-title"
      {...modalDismissProps(onClose)}
    >
      <div className="scc-drawer-heading">
        <div>
          <p className="scc-drawer-eyebrow">{eyebrow}</p>
          <h2 id="scc-capability-title">{reading.title}</h2>
        </div>
        <button autoFocus onClick={onClose} aria-label="关闭">
          关闭 ×
        </button>
      </div>
      <div className="scc-drawer-body">
        {reading.pending && (
          <p className="scc-notice" data-content-review="PENDING_OWNER">
            专业说明待审核：原需求完整语义与 F2 关系仍待 Owner
            审定。以下研究资料不构成正式绑定修复。
          </p>
        )}
        <section className="scc-drawer-section">
          <h3>在当前问题中做什么</h3>
          <p>{reading.purpose}</p>
        </section>
        <div className="scc-editorial-overview">
          <EditorialCapabilityImage path={reading.topic?.image} title={reading.title} />
          <div>
            <h3>它是什么</h3>
            <p>{reading.meaning}</p>
            {reading.topic && <p>{reading.topic.problem}</p>}
            <h3>能得到什么</h3>
            <p>{reading.output}</p>
          </div>
        </div>
        <section className="scc-drawer-section">
          <h3>可验证的产出类型</h3>
          <p>{reading.topic?.outputs ?? reading.output}</p>
        </section>
        <section className="scc-drawer-section">
          <h3>怎样实现</h3>
          <p>{reading.method}</p>
          {reading.topic && (
            <p className="scc-drawer-note">
              技术形态与配置举例：{reading.topic.example}
              。技术示例，不是已部署装备；图中设备不标注厂商型号。
            </p>
          )}
        </section>
        <section className="scc-drawer-section">
          <h3>适用条件与限制</h3>
          <p>{reading.limit}</p>
          <p data-capability-condition>{reading.condition}</p>
          {reading.scopeNote && (
            <p className="scc-drawer-scope" data-scope-note>
              {reading.scopeNote}
            </p>
          )}
        </section>
        {reading.topic && (
          <section className="scc-drawer-section">
            <h3>权威资料／官方技术参考</h3>
            <EditorialSources topic={reading.topic} />
          </section>
        )}
        {children}
      </div>
    </dialog>
  );
}

export function SupplementaryCapabilityTopic({ topic }: { readonly topic: EditorialTopic }) {
  return (
    <details className="scc-supplementary-topic" data-supplementary-topic={topic.key}>
      <summary>相关技术参考：{topic.title}（研究说明）</summary>
      <p>仅解释一种信息获取或辅助方式，未覆盖或绑定当前完整能力需求。</p>
      <EditorialCapabilityImage path={topic.image} title={topic.title} />
      <p>{topic.meaning}</p>
      <p>产出：{topic.outputs}</p>
      <p>实现：{topic.method}</p>
      <p>限制：{topic.limits}</p>
      <EditorialSources topic={topic} />
    </details>
  );
}
